import { useCallback, useEffect, useRef, useState } from "react";
import type { FormEvent, KeyboardEvent, RefObject } from "react";
import {
  PROBLEM_CODES,
  api,
  envelopeOverrun,
  errorMessage,
  problemCode,
  useApiMutation,
  useIdempotencyKey,
} from "../api";
import type { Int64, MissionProgress } from "../api";
import { Alert, Badge, Button, Card, Field, Stat, cx, useToast } from "../components";
import {
  formatDateTime,
  formatPercent,
  formatTokens,
  isZeroAmount,
  ratio,
  subAmounts,
} from "../format";
import { useShake } from "../motion";
import { useWalletScope } from "../wallet";
import { MissionsAffectation } from "./MissionsAffectation";
import {
  depassement,
  enveloppeEpuisee,
  erreurConsommation,
  erreurImputation,
  lireTokens,
  relicatRelache,
} from "./MissionsGestes";
import styles from "./MissionsPage.module.css";

/**
 * Les gestes que le contrat offre sur une mission en cours : les trois du
 * portefeuille, et l'affectation, réservée à l'ESN.
 */
export type Geste = "consommer" | "cloturer" | "annuler" | "affecter";

export type MissionsCarteProps = {
  /** Une mission **en cours** : c'est le seul statut qui accepte un geste. */
  mission: MissionProgress;
  /** Geste ouvert sur cette carte, ou `null`. Un seul panneau ouvert à la fois. */
  geste: Geste | null;
  /** Ouvre ou referme un panneau. `null` referme. */
  onGeste: (geste: Geste | null) => void;
  /** Un geste a abouti : la liste et les seaux du portefeuille sont à relire. */
  onFait: () => void;
};

/**
 * Une mission en cours, et les trois gestes qu'on peut encore lui faire.
 *
 * L'écran ne propose jamais une action que le service refusera : cette carte
 * n'est rendue que pour une mission `ordered` (une mission close rendrait
 * `409 invalid_transition`), et « Consommer » disparaît quand le reliquat est
 * nul — toute consommation y serait un dépassement d'enveloppe. Un bouton qui
 * ne sert qu'à obtenir un refus est une frustration gratuite.
 */
export function MissionsCarte({ mission, geste, onGeste, onFait }: MissionsCarteProps) {
  const toast = useToast();
  const { permissions } = useWalletScope();

  const consommation = useApiMutation(api.consumeMission);
  const cloture = useApiMutation(api.completeMission);
  const annulation = useApiMutation(api.cancelMission);

  /*
   * ------------------------------------------------------------------------
   * Cycle de vie de la clé d'idempotence
   * ------------------------------------------------------------------------
   * `consumeMission` l'exige, et le type l'impose. Elle porte l'identité du
   * **fait imputé** — en production, la ligne de feuille de temps ; ici, une
   * clé tirée au sort une fois par intention.
   *
   * `deps = [mission.id]` : elle est propre à cette mission, et ne bouge pas
   * d'un rendu à l'autre — une relecture de la liste ne la renouvelle donc pas.
   *
   * Elle ne change **pas** quand on corrige le montant après un refus : le
   * serveur a répondu sans rien appliquer, la clé reste libre, et le nouveau
   * montant part avec elle sans risque.
   *
   * Elle ne change surtout **pas** après un échec réseau : c'est exactement le
   * cas où on ignore si l'imputation est passée. Rejouer la même clé rend ce
   * doute inoffensif — si elle était passée, le serveur rend la mission
   * inchangée au lieu d'imputer une seconde fois.
   *
   * Elle se renouvelle **après un succès seulement** (`renew()`) : la
   * prochaine imputation sur la même mission est une autre journée de travail,
   * pas un rejeu. Sans ce renouvellement, elle serait ignorée en silence.
   */
  const { key: cleIdempotence, renew: renouvelerCle } = useIdempotencyKey([mission.id]);

  // La secousse porte sur le panneau : c'est le geste en cours qui est refusé.
  // La même ref sert d'ancre de focus, ce qui évite d'en poser deux.
  const { ref: refPanneau, shake } = useShake<HTMLDivElement>();
  const refCarte = useRef<HTMLElement | null>(null);
  const refSaisie = useRef<HTMLInputElement | null>(null);

  const [saisieConsommation, setSaisieConsommation] = useState("");
  const [saisieImputation, setSaisieImputation] = useState("0");
  const [soumis, setSoumis] = useState(false);

  const reliquat = mission.remaining_tokens;
  const epuisee = enveloppeEpuisee(reliquat);
  const enCours = consommation.pending || cloture.pending || annulation.pending;

  const idPanneau = `mission-${mission.id}-panneau`;
  const idTitrePanneau = `mission-${mission.id}-panneau-titre`;

  /**
   * Referme le panneau et ramène le focus sur le bouton qui l'avait ouvert.
   *
   * Sans ce retour, la fermeture au clavier renvoie le focus en tête de
   * document : l'utilisateur doit retraverser la page entière pour revenir où
   * il était. Le bouton est retrouvé dans la carte par son `data-geste`, faute
   * de pouvoir poser une `ref` sur `Button` — la primitive n'en expose pas, et
   * lui en ajouter une pour cet écran serait toucher à un bien commun.
   */
  const fermer = useCallback(
    (gesteFerme: Geste) => {
      onGeste(null);
      refCarte.current
        ?.querySelector<HTMLButtonElement>(`[data-geste="${gesteFerme}"]`)
        ?.focus();
    },
    [onGeste],
  );

  // Le panneau apparaît sans que la page bouge : au clavier comme au lecteur
  // d'écran, rien ne le signalerait. On y porte donc le focus — sur la saisie
  // quand il y en a une, sur le panneau lui-même pour la confirmation
  // d'annulation, qui n'a que du texte à faire lire avant le bouton rouge.
  useEffect(() => {
    if (geste === null) {
      return;
    }
    if (geste === "annuler" || geste === "affecter" || (geste === "cloturer" && epuisee)) {
      refPanneau.current?.focus();
      return;
    }
    refSaisie.current?.focus();
  }, [geste, epuisee, refPanneau]);

  function auClavier(evenement: KeyboardEvent<HTMLDivElement>) {
    // Échap referme, comme partout ailleurs. Un panneau qu'on ne peut fermer
    // qu'à la souris n'est pas utilisable au clavier.
    if (evenement.key === "Escape" && geste !== null) {
      evenement.stopPropagation();
      fermer(geste);
    }
  }

  function ouvrir(suivant: Geste) {
    // Changer de geste change d'intention : le refus affiché pour le
    // précédent ne concerne plus rien.
    consommation.reset();
    cloture.reset();
    annulation.reset();
    setSoumis(false);
    onGeste(geste === suivant ? null : suivant);
  }

  async function imputer(evenement: FormEvent<HTMLFormElement>) {
    evenement.preventDefault();
    setSoumis(true);
    if (enCours) {
      return;
    }

    const refus = erreurConsommation(saisieConsommation);
    const montant = lireTokens(saisieConsommation);
    if (refus !== null || montant === null) {
      shake();
      refSaisie.current?.focus();
      return;
    }

    const maj = await consommation.run(
      mission.id,
      { tokens: montant },
      { idempotencyKey: cleIdempotence },
    );
    if (maj === null) {
      shake();
      return;
    }

    // Succès : le fait est enregistré, la clé a joué son rôle. La prochaine
    // imputation est un autre fait — donc une autre clé.
    renouvelerCle();
    setSaisieConsommation("");
    setSoumis(false);
    toast.notify({
      tone: "success",
      title: "Consommation imputée",
      description: `${formatTokens(montant)} tokens passés du réservé au consommé sur « ${mission.label} ».`,
    });
    onFait();
  }

  async function cloturer(evenement: FormEvent<HTMLFormElement>) {
    evenement.preventDefault();
    setSoumis(true);
    if (enCours) {
      return;
    }

    const saisie = epuisee ? "0" : saisieImputation;
    const refus = erreurImputation(saisie);
    const montant = lireTokens(saisie);
    if (refus !== null || montant === null) {
      shake();
      refSaisie.current?.focus();
      return;
    }

    const relache = relicatRelache(reliquat, montant);
    const maj = await cloture.run(mission.id, { tokens: montant });
    if (maj === null) {
      shake();
      return;
    }

    setSaisieImputation("0");
    setSoumis(false);
    toast.notify({
      tone: "success",
      title: "Mission close",
      description: `${formatTokens(relache)} tokens relâchés vers le disponible${
        montant === "0" ? "" : `, après une imputation finale de ${formatTokens(montant)}`
      }.`,
    });
    onFait();
  }

  async function annuler() {
    if (enCours) {
      return;
    }
    const maj = await annulation.run(mission.id);
    if (maj === null) {
      shake();
      return;
    }
    toast.notify({
      tone: "success",
      title: "Mission annulée",
      description: `${formatTokens(reliquat)} tokens relâchés vers le disponible.`,
    });
    onFait();
  }

  return (
    <Card
      as="article"
      ref={refCarte}
      data-anim="card"
      className={styles.carte}
      title={mission.label}
      subtitle={`Ouvert le ${formatDateTime(mission.created_at)}`}
      actions={
        <Badge tone="info" dot>
          En cours
        </Badge>
      }
    >
      <div className={styles.chiffres}>
        <Stat
          label="Budget"
          value={<span className="lde-tabular">{formatTokens(mission.envelope_tokens)}</span>}
          unit="tokens"
          hint="Réservé à la création du projet."
        />
        <Stat
          label="Consommé"
          value={<span className="lde-tabular">{formatTokens(mission.consumed_tokens)}</span>}
          unit="tokens"
          bucket="consumed"
          highlight={mission.consumed_tokens}
          // `percent` est un nombre JSON borné entre 0 et 100, pas un montant :
          // il se divise sans précaution. Il fait foi sur un pourcentage
          // recalculé ici, puisqu'il est établi sur les montants exacts.
          ratio={mission.percent / 100}
          hint={`${formatPercent(mission.percent)} du budget consommé.`}
        />
        {/* Le reliquat est teinté « réservé » et non « disponible » : il est
            encore immobilisé dans le seau réservé, et n'en sortira qu'à la
            clôture ou à l'annulation. */}
        <Stat
          label="Reste"
          value={<span className="lde-tabular">{formatTokens(reliquat)}</span>}
          unit="tokens"
          bucket="reserved"
          highlight={reliquat}
          ratio={ratio(reliquat, mission.envelope_tokens)}
          hint={
            epuisee
              ? "Budget épuisé : plus rien à consommer."
              : "Encore réservé, pas encore dépensé."
          }
        />
      </div>

      <div className={styles.actions}>
        {epuisee ? (
          <p className={styles.mentionAction}>
            Enveloppe entièrement imputée : il ne reste qu'à clôturer, ce qui ne
            relâchera rien.
          </p>
        ) : (
          <Button
            data-geste="consommer"
            variant="primary"
            aria-expanded={geste === "consommer"}
            aria-controls={geste === "consommer" ? idPanneau : undefined}
            onClick={() => ouvrir("consommer")}
          >
            Consommer
          </Button>
        )}
        <Button
          data-geste="cloturer"
          variant="secondary"
          aria-expanded={geste === "cloturer"}
          aria-controls={geste === "cloturer" ? idPanneau : undefined}
          onClick={() => ouvrir("cloturer")}
        >
          Clôturer
        </Button>
        <Button
          data-geste="annuler"
          variant="ghost"
          aria-expanded={geste === "annuler"}
          aria-controls={geste === "annuler" ? idPanneau : undefined}
          onClick={() => ouvrir("annuler")}
        >
          Annuler
        </Button>
        {/* Les cartes ne sont montrées qu'à qui voit la facturation : l'ESN
            gère l'équipe, l'entreprise la lit. */}
        <Button
          data-geste="affecter"
          variant="ghost"
          aria-expanded={geste === "affecter"}
          aria-controls={geste === "affecter" ? idPanneau : undefined}
          onClick={() => ouvrir("affecter")}
        >
          {permissions.can_assign_missions ? "Gérer l'équipe" : "Équipe du projet"}
        </Button>
      </div>

      {geste !== null && (
        <div
          id={idPanneau}
          ref={refPanneau}
          className={styles.panneau}
          // `tabIndex={-1}` : hors de l'ordre de tabulation, mais focalisable
          // par programme à l'ouverture.
          tabIndex={-1}
          role="group"
          aria-labelledby={idTitrePanneau}
          onKeyDown={auClavier}
        >
          {geste === "consommer" && (
            <FormulaireConsommation
              idTitre={idTitrePanneau}
              refSaisie={refSaisie}
              reliquat={reliquat}
              saisie={saisieConsommation}
              onSaisie={(valeur) => {
                setSaisieConsommation(valeur);
                consommation.reset();
              }}
              soumis={soumis}
              erreur={consommation.error}
              pending={consommation.pending}
              onEnvoyer={(e) => void imputer(e)}
              onRenoncer={() => fermer("consommer")}
            />
          )}

          {geste === "cloturer" && (
            <FormulaireCloture
              idTitre={idTitrePanneau}
              refSaisie={refSaisie}
              reliquat={reliquat}
              epuisee={epuisee}
              saisie={saisieImputation}
              onSaisie={(valeur) => {
                setSaisieImputation(valeur);
                cloture.reset();
              }}
              soumis={soumis}
              erreur={cloture.error}
              pending={cloture.pending}
              onEnvoyer={(e) => void cloturer(e)}
              onRenoncer={() => fermer("cloturer")}
            />
          )}

          {geste === "affecter" && (
            <MissionsAffectation
              idTitre={idTitrePanneau}
              mission={mission}
              editable={permissions.can_assign_missions}
              onFait={onFait}
              onRenoncer={() => fermer("affecter")}
            />
          )}

          {geste === "annuler" && (
            <ConfirmationAnnulation
              idTitre={idTitrePanneau}
              libelle={mission.label}
              reliquat={reliquat}
              erreur={annulation.error}
              pending={annulation.pending}
              onConfirmer={() => void annuler()}
              onRenoncer={() => fermer("annuler")}
            />
          )}
        </div>
      )}
    </Card>
  );
}

type ProprietesConsommation = {
  idTitre: string;
  refSaisie: RefObject<HTMLInputElement | null>;
  reliquat: Int64;
  saisie: string;
  onSaisie: (valeur: string) => void;
  soumis: boolean;
  erreur: unknown;
  pending: boolean;
  onEnvoyer: (evenement: FormEvent<HTMLFormElement>) => void;
  onRenoncer: () => void;
};

/**
 * Imputer du travail réel.
 *
 * Le reliquat est annoncé **avant** l'envoi, et le dépassement chiffré dès la
 * frappe. Le bouton reste actif malgré l'avertissement : le reliquat affiché
 * peut dater de quelques secondes, et c'est le grand livre qui tranche — comme
 * sur l'écran de commande, l'écran dit ce qui va se passer, il n'interdit pas
 * d'essayer.
 */
function FormulaireConsommation({
  idTitre,
  refSaisie,
  reliquat,
  saisie,
  onSaisie,
  soumis,
  erreur,
  pending,
  onEnvoyer,
  onRenoncer,
}: ProprietesConsommation) {
  const montant = lireTokens(saisie);
  const refusLocal = soumis ? erreurConsommation(saisie) : null;
  const trop = montant === null ? null : depassement(montant, reliquat);

  return (
    <form className={styles.formulaire} onSubmit={onEnvoyer} noValidate>
      <p id={idTitre} className={styles.panneauTitre}>
        Imputer du travail réel
      </p>

      <fieldset className={styles.bloc} disabled={pending}>
        <Field
          label="Tokens consommés"
          required
          error={refusLocal}
          hint={`Reliquat disponible sur l'enveloppe : ${formatTokens(reliquat)} tokens. Au-delà, le service refuse : il ne pioche jamais dans le disponible.`}
        >
          {(controle) => (
            <input
              {...controle}
              ref={refSaisie}
              // `text` et non `number` : un montant du contrat est une chaîne
              // décimale, et un champ numérique le ferait transiter par un
              // `number` — l'arrondi que tout le projet s'interdit.
              type="text"
              inputMode="decimal"
              autoComplete="off"
              placeholder="ex. 3,5"
              value={saisie}
              onChange={(evenement) => onSaisie(evenement.target.value)}
            />
          )}
        </Field>

        {trop !== null && (
          <p className={styles.avertissement} role="status">
            Ce montant dépasse le reliquat de{" "}
            <strong className="lde-tabular">{formatTokens(trop)}</strong> tokens :
            le service le refusera.
          </p>
        )}

        {erreur !== null && <Refus erreur={erreur} />}

        <div className={styles.actionsPanneau}>
          <Button type="submit" variant="primary" loading={pending}>
            {montant === null
              ? "Imputer"
              : `Imputer ${formatTokens(montant)} tokens`}
          </Button>
          <Button variant="ghost" onClick={onRenoncer}>
            Renoncer
          </Button>
        </div>
      </fieldset>

      {/* La clé reste dans le code de la requête : la montrer au client ne lui
          apprenait rien, la garantie se dit en une phrase. */}
      <p className={styles.mentionCle}>
        Un double clic, ou un renvoi après une coupure, n'est compté qu'une fois.
      </p>
    </form>
  );
}

type ProprietesCloture = {
  idTitre: string;
  refSaisie: RefObject<HTMLInputElement | null>;
  reliquat: Int64;
  epuisee: boolean;
  saisie: string;
  onSaisie: (valeur: string) => void;
  soumis: boolean;
  erreur: unknown;
  pending: boolean;
  onEnvoyer: (evenement: FormEvent<HTMLFormElement>) => void;
  onRenoncer: () => void;
};

/**
 * Clôturer : une imputation finale facultative, puis le retour du reliquat.
 *
 * Le montant qui reviendra au disponible est annoncé avant l'envoi et se
 * recalcule à chaque frappe. C'est la conservation des tokens rendue visible —
 * rien ne s'évapore, ce qui n'a pas été consommé retourne d'où il venait — et
 * c'est ce qui rend le grand livre convaincant en démonstration.
 */
function FormulaireCloture({
  idTitre,
  refSaisie,
  reliquat,
  epuisee,
  saisie,
  onSaisie,
  soumis,
  erreur,
  pending,
  onEnvoyer,
  onRenoncer,
}: ProprietesCloture) {
  const montant = epuisee ? "0" : lireTokens(saisie);
  const refusLocal = soumis && !epuisee ? erreurImputation(saisie) : null;
  const trop = montant === null ? null : depassement(montant, reliquat);
  const relache = montant === null ? reliquat : relicatRelache(reliquat, montant);
  const rienARelacher = isZeroAmount(relache);

  return (
    <form className={styles.formulaire} onSubmit={onEnvoyer} noValidate>
      <p id={idTitre} className={styles.panneauTitre}>
        Clôturer la mission
      </p>

      <fieldset className={styles.bloc} disabled={pending}>
        {/* Enveloppe épuisée : pas de champ du tout. Une imputation finale y
            serait refusée, et le paragraphe ci-dessous dit déjà ce qui reste à
            faire. */}
        {!epuisee && (
          <Field
            label="Imputation finale"
            hint={`Facultative : 0 signifie « rien à imputer, on clôture en l'état ». Reliquat d'enveloppe : ${formatTokens(reliquat)} tokens.`}
            error={refusLocal}
          >
            {(controle) => (
              <input
                {...controle}
                ref={refSaisie}
                type="text"
                inputMode="decimal"
                autoComplete="off"
                value={saisie}
                onChange={(evenement) => onSaisie(evenement.target.value)}
              />
            )}
          </Field>
        )}

        {trop !== null && (
          <p className={styles.avertissement} role="status">
            Cette imputation dépasse le reliquat de{" "}
            <strong className="lde-tabular">{formatTokens(trop)}</strong> tokens :
            le service refusera la clôture.
          </p>
        )}

        <p className={styles.conservation}>
          {trop !== null ? (
            <>Tant que l'imputation dépasse le reliquat, rien ne sera relâché.</>
          ) : rienARelacher ? (
            // « 0 tokens reviendront au disponible » se lit deux fois avant
            // d'être compris : quand il n'y a rien à rendre, on le dit en toutes
            // lettres plutôt qu'avec un zéro au milieu d'une phrase.
            <>
              {montant !== null && montant !== "0" ? (
                <>
                  L'imputation finale de{" "}
                  <strong className="lde-tabular">{formatTokens(montant)}</strong>{" "}
                  tokens absorbe tout le reliquat : rien ne reviendra au
                  disponible.
                </>
              ) : (
                <>
                  L'enveloppe est entièrement imputée : la clôture ne relâchera
                  rien, tout est déjà passé au consommé.
                </>
              )}
            </>
          ) : (
            <>
              À la clôture,{" "}
              <strong className="lde-tabular">{formatTokens(relache)}</strong>{" "}
              tokens quitteront le seau réservé pour revenir au{" "}
              <strong>disponible</strong>
              {montant !== null && montant !== "0" && (
                <>
                  , et{" "}
                  <strong className="lde-tabular">{formatTokens(montant)}</strong>{" "}
                  passeront au consommé
                </>
              )}
              . Rien ne s'évapore.
            </>
          )}
        </p>

        {erreur !== null && <Refus erreur={erreur} />}

        <div className={styles.actionsPanneau}>
          <Button type="submit" variant="primary" loading={pending}>
            {trop !== null || rienARelacher
              ? "Clôturer la mission"
              : `Clôturer et relâcher ${formatTokens(relache)} tokens`}
          </Button>
          <Button variant="ghost" onClick={onRenoncer}>
            Renoncer
          </Button>
        </div>
      </fieldset>
    </form>
  );
}

type ProprietesAnnulation = {
  idTitre: string;
  libelle: string;
  reliquat: Int64;
  erreur: unknown;
  pending: boolean;
  onConfirmer: () => void;
  onRenoncer: () => void;
};

/**
 * Annuler : irréversible, donc confirmé.
 *
 * Deux gestes plutôt qu'un, et le bouton de confirmation n'est pas celui qui a
 * ouvert le panneau : une annulation ne doit pas pouvoir partir d'un double
 * clic malheureux. Tout se fait au clavier — le focus arrive sur le texte, la
 * tabulation mène au bouton rouge, Échap renonce.
 */
function ConfirmationAnnulation({
  idTitre,
  libelle,
  reliquat,
  erreur,
  pending,
  onConfirmer,
  onRenoncer,
}: ProprietesAnnulation) {
  return (
    <div className={styles.formulaire}>
      <p id={idTitre} className={styles.panneauTitre}>
        Annuler « {libelle} » ?
      </p>

      <p className={styles.conservation}>
        <strong className="lde-tabular">{formatTokens(reliquat)}</strong> tokens,
        le reliquat et non l'enveloppe entière, repartiront au{" "}
        <strong>disponible</strong>. Ce qui a déjà été consommé reste consommé.
      </p>

      <p className={cx(styles.avertissement, styles.irreversible)}>
        Une mission annulée ne se rouvre pas. Pour arrêter une prestation déjà
        commencée en gardant trace du travail fait, préférez la clôture.
      </p>

      {erreur !== null && <Refus erreur={erreur} />}

      <div className={styles.actionsPanneau}>
        <Button variant="danger" loading={pending} onClick={onConfirmer}>
          Annuler la mission
        </Button>
        <Button variant="secondary" onClick={onRenoncer}>
          Conserver la mission
        </Button>
      </div>
    </div>
  );
}

/**
 * Un refus du service, chiffré quand il l'est.
 *
 * Le comportement se branche sur le `code` machine, jamais sur le texte, qui
 * est traduisible. `envelope_overrun` porte `requested` et `remaining`
 * précisément pour qu'on écrive « il ne reste que N tokens » au lieu de « une
 * erreur est survenue ».
 */
function Refus({ erreur }: { erreur: unknown }) {
  const trop = envelopeOverrun(erreur);
  if (trop !== null) {
    return (
      <Alert
        role="alert"
        title={
          <>
            Il ne reste que <strong>{formatTokens(trop.remaining)}</strong> tokens sur cette enveloppe.
          </>
        }
      >
        La demande portait sur <strong>{formatTokens(trop.requested)}</strong> tokens, soit{" "}
        <strong>{formatTokens(subAmounts(trop.requested, trop.remaining))}</strong> de trop. Rien n'a été
        imputé : un dépassement est refusé, jamais absorbé sur le disponible. Baissez le montant, ou
        commandez une seconde prestation.
      </Alert>
    );
  }

  if (problemCode(erreur) === PROBLEM_CODES.invalidTransition) {
    return (
      <Alert role="alert" title="Ce projet n'est plus en cours.">
        Il a été clos ou annulé ailleurs depuis l'affichage de cette liste, et un projet clos ne se
        rouvre pas. Relisez les projets pour voir son état réel.
      </Alert>
    );
  }

  return (
    <Alert role="alert" title="Geste refusé">
      {errorMessage(erreur)}
    </Alert>
  );
}
